// Tables belong to a school (no universal tables)
// Input Type is one of the following:
//  NPCInput = A Question Id.  The answer for the question becomes our input
//  NPCIntermediate = Costs & Packaging Items
//  InasResult = Result Value Keys

import { Formula } from '../_models/formula';
import { Question } from '../_models/question';

export interface Table {
    tableId: string; // "table_Id" from legacy db.  Used as Doc Id
    name: string; // "Travel Allowance"
    description: string; // "Travel allowance by state - dependent student"

    type: 'Question' | 'Calculation' | 'Formula'; // TODO: added this and need to set it.
    keyId?: string; // "key_id": "student.residencyStateCode"
    question?: Question; // TODO: Added this, do we need to update anything?
    formulaId?: string;
    formula?: Formula;

    rangeTest: boolean; // True if searching for an Amount in a range, False if searching for a match on the Code
    lookupTests?: LookupTest[];
    rangeTests?: RangeTest[];

    /**
     * When present, the table is a two-axis matrix: resolve row and column inputs to indices,
     * then read `values[row * columnBinCount + col]`. Legacy 1D fields above are unused for this mode.
     *
     * Contract (enforced on save): breakpoints ascending; `values.length === rowBins * columnBins`.
     */
    matrixTable?: MatrixTable;
}

//  Each test has either a LookupValue or a Range Start and Finish
export interface LookupTest {
    // label?: string;
    lookupValue: string; // "code"
    result: number; // "value"
    order: number; // "ordinal"
}
// TODO: Ranges don't need a start and a finish.  Just do a search that looks for closest but less than.
export interface RangeTest {
    rangeStart: number;
    rangeFinish: number;
    result: number;
    order: number; // "ordinal"
}

/**
 * One dimension of a matrix table (row bands or column bands).
 * Input from keyId or formulaId, same idea as 1D Table.
 *
 * Use exactly one of breakpoints or lookupValues:
 * • breakpoints — **sorted ascending** (saved data). Band count **equals** length; same as the
 *   corresponding grid edge size. Band i: `[breakpoints[i], breakpoints[i+1])` for `i < n-1`,
 *   else `[breakpoints[n-1], ∞)`. Runtime clamps below the first floor to bin 0; at/above the last floor to bin n−1.
 * • lookupValues — ordered strings; exact match (case-insensitive). Band count = length.
 */
export interface MatrixAxis {
    keyId?: string;
    formulaId?: string;
    formula?: Formula;
    breakpoints?: number[];
    lookupValues?: string[];
}

export interface MatrixTable {
    /** Column dimension: band count = number of columns in the value grid. */
    columns: MatrixAxis;
    /** Row dimension: band count = number of rows in the value grid. */
    rows: MatrixAxis;
    /**
     * Row-major cell values: `values[rowIndex * columnBinCount + colIndex]`
     * where `columnBinCount === breakpoints.length` or `lookupValues.length` on `columns`
     * (and similarly for row bins from `rows`). Firestore-safe (single array of numbers).
     */
    values: number[];
}

/** Row-major index into `MatrixTable.values`. */
export function matrixValueIndex(columnBinCount: number, rowIndex: number, colIndex: number): number {
    return rowIndex * columnBinCount + colIndex;
}

/** Keys that reference runtime/INAR calculations, not NPC questions (matrix axis input). */
export function isMatrixAxisCalculationKey(keyId?: string): boolean {
    if (!keyId) {
        return false;
    }
    return keyId.startsWith('rt.') || keyId.startsWith('inar');
}

export interface TableInputReference {
    entityType: 'Question' | 'Formula';
    entityId: string;
}

/** Question and formula inputs referenced by a table (1D or matrix). */
export function getTableInputReferences(table: Table): TableInputReference[] {
    const refs: TableInputReference[] = [];
    if (table.matrixTable) {
        for (const axis of [table.matrixTable.rows, table.matrixTable.columns]) {
            if (axis.formulaId) {
                refs.push({ entityType: 'Formula', entityId: axis.formulaId });
            }
            if (axis.keyId && !isMatrixAxisCalculationKey(axis.keyId)) {
                refs.push({ entityType: 'Question', entityId: axis.keyId });
            }
        }
        return refs;
    }
    if (table.type === 'Formula' && table.formulaId) {
        refs.push({ entityType: 'Formula', entityId: table.formulaId });
    }
    if (table.type === 'Question' && table.keyId) {
        refs.push({ entityType: 'Question', entityId: table.keyId });
    }
    return refs;
}

/** Empty matrix used when creating a new matrix table (single cell until axes are configured). */
export function createEmptyMatrixTable(): MatrixTable {
    return {
        columns: { breakpoints: [0] },
        rows: { breakpoints: [0] },
        values: [0],
    };
}

/** Mutates `table` for matrix mode: sets `matrixTable` and clears 1D row data. */
export function applyNewMatrixTableLayout(table: Table): void {
    table.matrixTable = createEmptyMatrixTable();
    table.rangeTests = undefined;
    table.lookupTests = undefined;
    table.rangeTest = true;
}

/** Mutates `table` for standard 1D mode: removes `matrixTable` and clears 1D rows (same as a newly created standard table before input is applied). */
export function applyStandardTableLayout(table: Table): void {
    delete table.matrixTable;
    table.rangeTest = true;
    table.lookupTests = undefined;
    table.rangeTests = undefined;
}
